<script setup lang="ts">
import { ref, watch, computed, ComputedRef } from 'vue';
import supportingInfoContainer from './supportingInfoContainer.vue';
import { getTranslation } from '@/utils/data-display-utils';
import { Section } from '@/store/section.interface';
import { useCommunityStore } from '@/store/communityStore';
import { QExpansionItem } from 'quasar';
import { getLabel } from '@/utils/data-display-utils';
import { getAltText } from '@/utils/image.helper';
import { StoredFile } from '@/store/common.interface';
import { Locales } from '@/store/language.interface';
import BaseButton from '@/components/base/BaseButton.vue';

// STORE
const communityStore = useCommunityStore();

// PROPS
const props = defineProps<{
  sections: Array<Section>;
  activeSection: Section;
  consultationBanner?: StoredFile;
  context?: string;
  contextAttachment: Array<any>;
  contextOpened: boolean | null;
  attachmentsOpened: boolean;
  languageEnabled?: Locales[];
}>();

// REFS
const theme = ref<number>(props.activeSection.orderNum);
const contextOpenedRef = ref<boolean | null>(props.activeSection.contextOpened);

// EMITS
const emit = defineEmits<{
  (e: 'changeSection', newValue: number): void;
  (e: 'openAttachmentDialog', section_id: number): void;
}>();

// FUNCTIONS
function changeSection(id: number) {
  emit('changeSection', id);
}

function goToPreviousSection() {
  const currentIndex = props.sections.findIndex((section) => section.orderNum === theme.value);
  if (currentIndex <= 0) return;
  theme.value = props.sections[currentIndex - 1].orderNum;
}

function goToNextSection() {
  const currentIndex = props.sections.findIndex((section) => section.orderNum === theme.value);
  if (currentIndex === -1 || currentIndex >= props.sections.length - 1) return;
  theme.value = props.sections[currentIndex + 1].orderNum;
}

function getSectionButtonLabel(section: Section, index: number): string {
  const title =
    getTranslation(section, 'title', props.languageEnabled) || getLabel('consultation.section', props.languageEnabled);
  return getLabel('aria-label.consultation.section-selector', props.languageEnabled, {
    named: {
      current:
        section.orderNum === theme.value
          ? getLabel('aria-label.consultation.current-section', props.languageEnabled)
          : '',
      position: index + 1,
      title,
      total: props.sections.length,
    },
  }).trim();
}

function getArrowButtonLabel(direction: 'previous' | 'next'): string {
  return getLabel(`aria-label.consultation.${direction}-section`, props.languageEnabled);
}

function openAttachmentDialog() {
  emit('openAttachmentDialog', props.activeSection.id);
}

// Watch for changes in the theme ref
watch(theme, (newValue) => {
  changeSection(newValue);
});

watch(props, (newValue) => {
  theme.value = props.sections.find((s) => s.orderNum === newValue.activeSection.orderNum)?.orderNum;
  contextOpenedRef.value = newValue.activeSection.contextOpened;
});

// COMPUTED
const themeCount = computed(() => {
  return props.sections.length;
});

const activeThemeIndex = computed(() => props.sections.findIndex((section) => section.orderNum === theme.value));

const hasPreviousSection = computed(() => activeThemeIndex.value > 0);

const hasNextSection = computed(
  () => activeThemeIndex.value >= 0 && activeThemeIndex.value < props.sections.length - 1,
);

const getBanner: ComputedRef<StoredFile> = computed(() => {
  return props.activeSection?.bannerFile?.url ? props.activeSection.bannerFile : props.consultationBanner;
});
</script>

<template>
  <div class="section-card" id="sectionContainer">
    <div class="section-card__banner">
      <img :src="getBanner.url" :alt="getAltText(getBanner, 'alt-text.section.banner', props.languageEnabled)" />
    </div>
    <div class="section-card__header">
      <q-carousel v-model="theme" swipeable animated control-color="primary" padding class="rounded-borders">
        <q-carousel-slide class="column flex-center" v-for="(theme, index) in props.sections" :name="theme.orderNum">
          <div class="carousel">
            <div class="carousel-title">
              <h3>{{ getTranslation(theme, 'title', props.languageEnabled) }}</h3>
            </div>
            <div class="carousel-count">{{ index + 1 }}/{{ themeCount }}</div>
          </div>
        </q-carousel-slide>
      </q-carousel>
      <div
        class="section-card__navigation"
        :aria-label="getLabel('consultation.sections', props.languageEnabled, { plural: themeCount })"
      >
        <BaseButton
          class="section-card__arrow"
          isIconOnly
          isTransparent
          icon="chevron_left"
          :aria-label="getArrowButtonLabel('previous')"
          :title="getArrowButtonLabel('previous')"
          :disabled="!hasPreviousSection"
          @click="goToPreviousSection"
        />
        <div
          class="section-card__dots"
          role="group"
          :aria-label="getLabel('aria-label.consultation.section-navigation', props.languageEnabled)"
        >
          <button
            v-for="(section, index) in props.sections"
            :key="section.id"
            class="section-card__dot"
            :class="{ 'section-card__dot--active': section.orderNum === theme }"
            type="button"
            :aria-label="getSectionButtonLabel(section, index)"
            :aria-pressed="section.orderNum === theme"
            :aria-current="section.orderNum === theme ? 'true' : undefined"
            @click="theme = section.orderNum"
          >
            <span class="sr-only">{{ getSectionButtonLabel(section, index) }}</span>
          </button>
        </div>
        <BaseButton
          class="section-card__arrow"
          isIconOnly
          isTransparent
          icon="chevron_right"
          :aria-label="getArrowButtonLabel('next')"
          :title="getArrowButtonLabel('next')"
          :disabled="!hasNextSection"
          @click="goToNextSection"
        />
      </div>
    </div>
    <div class="accordion">
      <q-list bordered>
        <q-expansion-item v-model="contextOpenedRef">
          <template v-slot:header>
            <q-item-section avatar>
              <q-icon name="o_description"></q-icon>
            </q-item-section>
            <q-item-section>
              {{ getLabel('context.context', props.languageEnabled) }}
            </q-item-section>
          </template>
          <template v-slot>
            <q-card>
              <q-card-section>
                <div class="context-container__description">
                  {{ props.context }}
                </div>
              </q-card-section>
            </q-card>
          </template>
        </q-expansion-item>
        <q-expansion-item v-bind:default-opened="props.attachmentsOpened" v-if="props.contextAttachment.length >= 1">
          <template v-slot:header>
            <q-item-section avatar>
              <q-icon name="link"></q-icon>
            </q-item-section>

            <q-item-section>
              <div class="attachments">
                <div>
                  {{
                    getLabel('context.context-attachment', props.languageEnabled, {
                      named: { count: props.contextAttachment.length },
                    })
                  }}
                </div>
                <div class="attachments_count">[ {{ props.contextAttachment.length }} ]</div>
              </div>
            </q-item-section>
          </template>
          <template v-slot>
            <q-card>
              <q-card-section>
                <supporting-info-container
                  :supportingInfos="props.contextAttachment"
                  :language-enabled="props.languageEnabled"
                  @open-attachment-dialog="openAttachmentDialog"
                />
              </q-card-section>
            </q-card>
          </template>
        </q-expansion-item>
      </q-list>
    </div>
  </div>
</template>

<style lang="scss">
.section-card {
  font-size: 16px;

  &__header {
    width: 80%;
    margin: 0 10%;
    background-color: $color-neutral-white;
    border-radius: 0 0 8px 8px;
    overflow: hidden;
  }

  &__banner {
    display: flex;
    width: inherit;
    margin: 0 10%;

    img {
      width: 100%;
      height: 100%;
      border-radius: 8px 8px 0px 0px;
    }
  }

  &__navigation {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $space-md;
    padding: 0 $space-lg $space-md;
  }

  &__dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $space-sm;
  }

  &__dot {
    width: 18px;
    height: 18px;
    border-radius: 999px;
    border: 2px solid $color-primary;
    background-color: $color-neutral-white;
    transition:
      background-color 200ms ease,
      transform 200ms ease,
      box-shadow 200ms ease;

    &:hover {
      background-color: $color-primary-12;
    }

    &:focus-visible {
      outline: none;
      box-shadow:
        0 0 0 2px $color-neutral-white,
        0 0 0 4px var(--q-primary);
      transform: scale(1.05);
    }

    &--active {
      background-color: $color-primary;
    }
  }

  &__arrow.q-btn.base-button {
    min-width: 36px;
    min-height: 36px;
    padding: 0;
    border-radius: 999px;
    color: $color-neutral-black;

    &:hover {
      background-color: $color-neutral-grey-100;
    }

    &.is-disabled {
      background-color: transparent !important;
    }
  }

  .accordion {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: inherit;
    margin: 0 10%;
    background-color: $color-neutral-white;
    border-radius: 0px 0px 8px 8px;
    width: 80%;

    .q-list {
      width: inherit;
    }
    .q-expansion-item {
      border-radius: 8px;
      &:hover {
        background-color: $color-neutral-grey-100;
        font-weight: bold;
      }
    }
    .q-item.q-item-type {
      padding: $space-sm $space-md;
      height: auto;
      display: flex;
      align-content: center;
      flex-wrap: wrap;

      .q-icon {
        font-size: $small-title;
        padding: 0 $space-sm;
      }
    }

    .q-expansion-item__content .q-card {
      border-radius: 0px 0px 8px 8px;
      font-size: $small-text;
      .base-media__descriptive-text {
        align-content: end;
        font-size: $small-text;
      }
      .q-card__section--vert {
        padding-top: 0;
      }
    }
  }
  .attachments {
    display: flex;
    flex-direction: row;
    gap: $space-sm;

    &_count {
      display: flex;
      align-items: center;
      font-weight: bold;
      white-space: nowrap;
    }
  }
}

.sr-only {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
}

.q-carousel {
  background-color: transparent !important;
  margin: unset;

  &__slide {
    width: 100% !important;
    background-color: transparent;
    padding: $space-md !important;
    padding-bottom: $space-sm !important;
  }

  .q-panel {
    > div {
      min-height: 120px !important;
    }
  }
}

.q-btn--round {
  background-color: $color-neutral-white !important;
}

.carousel {
  display: flex;
  flex-direction: row;
  gap: 10px;
  width: 100%;
  flex-grow: 1;

  &-title {
    display: flex;
    justify-content: center;
    width: inherit;
    align-content: center;
    flex-wrap: wrap;

    h3 {
      text-align: center;
    }
  }

  &-count {
    color: $color-primary;
    font-size: $body-text;
    font-weight: bold;
    width: 30px;
  }
}

@media screen and (max-width: $breakpoint-sm) {
  .q-carousel {
    &__slide {
      width: 100% !important;
    }

    // &__slides-container {
    //   max-width: 200px;
    // }

    .q-panel {
      display: flex !important;
      justify-content: center !important;
      width: auto;
      background-color: $color-neutral-white;

      > div {
        min-height: 150px !important;
      }
    }

    .carousel {
      margin-bottom: $space-md;
      max-width: 343px;
    }
  }
  .section-card {
    &__header {
      width: 100%;
      margin: 0;
    }

    &__navigation {
      gap: $space-sm;
      padding-left: $space-sm;
      padding-right: $space-sm;
    }

    &__banner {
      margin: unset;
    }
    .accordion {
      width: 100%;
      margin: unset;
    }
  }
}
</style>
